import { Flow } from 'flow-to-typescript-codemod';
import { useMemo } from 'react';
import analytics from 'modules/analytics';
import { globalContext } from 'modules/analytics/constants';
import { useSplitClient } from 'modules/feature-flags';
import { env } from 'config/env';
import { FFDefaults } from './featureFlags.generated';
import type { FeatureFlag } from './featureFlags.generated';

export function useSplitFlag<T = string>(
  flagName: FeatureFlag,
  castTo?: (arg1: string) => T
): [T, boolean];

export function useSplitFlag(flagName: FeatureFlag, castTo?: void): [string, boolean];

// @ts-expect-error [EN-10263] - TS7006 - Parameter 'flagName' implicitly has an 'any' type. | TS7006 - Parameter 'castTo' implicitly has an 'any' type.
export function useSplitFlag(flagName, castTo) {
  const [client, ready, timeout] = useSplitClient();
  // @ts-expect-error [EN-10263] - TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type 'Readonly<{ ANATOMIC_NAVIGATOR: "off"; AUTOLOAD_PRIORS: "on"; EDIT_CASE_PRIORITY: "on"; ENABLE_REPORTER_SUBMISSION: "on"; GSPS_ANNOTATIONS: "on"; HANGING_PROTOCOL_DESERIALIZATION: "legacy"; ... 61 more ...; ALLOW_MAMMO_SSC_FEATURES: "off"; }>'.
  const fallbackValue = FFDefaults[flagName];
  const loading = ready || env.STORYBOOK === 'true' ? false : !timeout;
  const flag = useMemo(() => {
    if (client == null && env.STORYBOOK === 'true') {
      return fallbackValue;
    }
    if (timeout || (ready && client == null)) {
      analytics.addContext(globalContext.workspace.featureFlag(flagName), fallbackValue);
      return fallbackValue;
    }
    if (ready && client != null) {
      let value = client.getTreatment(flagName);
      if (value === 'control') {
        value = fallbackValue;
      }
      analytics.addContext(globalContext.workspace.featureFlag(flagName), value);
      return value;
    }
    return fallbackValue;
  }, [client, flagName, ready, fallbackValue, timeout]);

  return [castTo ? castTo(flag) : flag, loading];
}

export const useSplitFlags = (
  flagNames: FeatureFlag[]
): [
  (
    | {
        [x: string]: string;
      }
    | null
    | undefined
  ),
  boolean,
] => {
  const [client, ready, timeout] = useSplitClient();
  const loading = ready ? false : !timeout;
  const flags = useMemo(() => {
    if (ready) {
      const values = client?.getTreatments(flagNames as string[]);
      if (values != null) {
        Object.keys(values).forEach((flag) => {
          analytics.addContext(globalContext.workspace.featureFlag(flag), values[flag]);
        });
      }
      return values;
    }
    return null;
  }, [client, flagNames, ready]);
  return [flags, loading];
};
export const withSplitFlags: (
  flagNames: FeatureFlag[],
  prop: string
) => (
  Component: Flow.AbstractComponent<
    {
      [p: string]: {
        loading: boolean;
        flags: unknown;
      };
    },
    unknown
  >
) => (props: Record<any, any>) => React.ReactNode =
  (flagNames, prop = 'treatments') =>
  (
    Component: Flow.AbstractComponent<
      {
        [p: string]: {
          flags: unknown;
          loading: boolean;
        };
      },
      unknown
    >
  ) =>
    function WithSplitFlags(props: Record<any, any>) {
      const [flags, loading] = useSplitFlags(flagNames);
      return <Component {...props} {...{ [prop]: { loading, flags } }} />;
    };
